import React, { useContext, useEffect, useRef, useState } from 'react';
import type { BaseSelectRef } from 'rc-select';
import { Form, Input, InputRef, Select } from 'antd';
import { IShowcase } from '../../../../interfaces/interfaces';
import { FormInstance } from 'antd/es/form';
import { FieldType } from '../../../../../usersPage/interfaces/interfaces';
import { getValidateCase } from '../../../../helpers/helpers';
import Types from '../../../../../../../types/types';

import s from '../../General/General.module.scss';
import { tablesSlice } from '../../../../TablesSlice';
import { useAppDispatch } from '../../../../../../core/redux';

const { errorEngNameMessage } = Types;

interface EditableCellProps {
  allEnNames: Array<string>;
  allNumbers: Array<number>;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof IShowcase;
  record: IShowcase;
  selectOptions: Array<{ label: string; value: string }>;
  // xPathCodes: Array<{ label: string; value: string }>;
  xPathOptions: Array<{ label: string; value: string }>;
  attributeTypeOptions: Array<{ name: string; id: number }>;
  handleSave: (record: IShowcase) => void;
}
const renderValidateInput = (
  dataIndex,
  inputRef,
  save,
  errMessage,
  condition,
  allEnNames,
  currentValue,
) => {
  return (
    <Form.Item<FieldType>
      name={dataIndex}
      rules={[
        {
          required: true,
          message: 'Обязательно для заполнения',
        },
        ({ getFieldValue }) => ({
          validator(_, value) {
            const hasInArr = currentValue !== value && allEnNames.includes(value);
            let cond = false;
            switch (condition) {
              case 'ru': {
                cond = getValidateCase(getFieldValue(dataIndex), 'ru');
                break;
              }
              case 'en': {
                cond = getValidateCase(getFieldValue(dataIndex), 'en');
                break;
              }
            }
            if (hasInArr) {
              return Promise.reject(new Error('Данное название уже используется'));
            }
            if (!value || cond) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(errMessage));
          },
        }),
      ]}
      className={s.field}
    >
      <Input style={{ width: '100%' }} ref={inputRef} onPressEnter={save} onBlur={save} />
    </Form.Item>
  );
};

const renderNumberValidateInput = (
  dataIndex,
  inputRef,
  save,
  errMessage,
  allNumbers,
  currentValue,
) => {
  return (
    <Form.Item<FieldType>
      name={dataIndex}
      rules={[
        {
          required: true,
          message: 'Обязательно для заполнения',
        },
        () => ({
          validator(_, value) {
            const val = value && currentValue != value && Number(value);
            const hasInArr = val && !!allNumbers.find((number) => number === val);
            if (!value || !hasInArr) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(errMessage));
          },
        }),
      ]}
      className={s.field}
    >
      <Input style={{ width: '100%' }} ref={inputRef} onPressEnter={save} onBlur={save} />
    </Form.Item>
  );
};
export const EditableContext = React.createContext<FormInstance<unknown> | null>(null);
const { setTargetShowcaseEditingField } = tablesSlice.actions;
export const EditableCell: React.FC<EditableCellProps> = ({
  allEnNames,
  allNumbers,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  selectOptions,
  xPathOptions,
  attributeTypeOptions,
  ...restProps
}) => {
  const dispatch = useAppDispatch();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const selectRef = useRef<BaseSelectRef>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing) {
      if (inputRef.current) {
        inputRef.current!.focus();
      }
      if (selectRef.current) {
        selectRef.current!.focus();
      }
    }
  }, [editing]);

  const toggleEdit = () => {
    dispatch(setTargetShowcaseEditingField(!editing));
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const values: object = (await form.validateFields()) || {};

      toggleEdit();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const number = values.number;
      if (number) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        values.number = Number(number);
      }
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  const renderEditInput = (dataIndex, allEnNames, allNumbers) => {
    switch (dataIndex) {
      case 'result': {
        return <Input style={{ width: '100%' }} ref={inputRef} onPressEnter={save} onBlur={save} />;
      }
      case 'description': {
        return (
          <Select
            style={{ width: '200px' }}
            ref={selectRef}
            onBlur={save}
            options={selectOptions}
          />
        );
      }
      case 'xpath': {
        return (
          <Select
            showSearch
            filterOption={(val, option) => {
              return option.label.toLocaleLowerCase().includes(val.toLocaleLowerCase());
            }}
            style={{ width: '200px' }}
            ref={selectRef}
            onBlur={save}
            options={xPathOptions}
          />
        );
      }
      case 'attributeType': {
        return (
          <Select
            style={{ width: '200px' }}
            ref={selectRef}
            onBlur={save}
            options={attributeTypeOptions}
            fieldNames={{ label: 'name', value: 'name' }}
          />
        );
      }
      case 'russianName': {
        const errMessage = 'Разрешен ввод только на русском языке';
        const currentValue = record[dataIndex];
        return renderValidateInput(dataIndex, inputRef, save, errMessage, 'ru', [], currentValue);
      }
      case 'englishName': {
        const currentValue = record[dataIndex];
        return renderValidateInput(
          dataIndex,
          inputRef,
          save,
          errorEngNameMessage,
          'en',
          allEnNames,
          currentValue,
        );
      }
      case 'number': {
        const errMessage = 'Данное номер уже используется';
        const currentValue = record[dataIndex];
        return renderNumberValidateInput(
          dataIndex,
          inputRef,
          save,
          errMessage,
          allNumbers,
          currentValue,
        );
      }
      default: {
        return <Input style={{ width: '100%' }} ref={inputRef} onPressEnter={save} onBlur={save} />;
      }
    }
  };
  if (editable && record.description !== 'custom') {
    childNode = editing ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex}>
        {renderEditInput(dataIndex, allEnNames, allNumbers)}
      </Form.Item>
    ) : (
      <div className='editable-cell-value-wrap' onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
